import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, X, Gift } from "lucide-react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { useStore } from "@/context/StoreContext";
import { cad, cadShort, imgUrl } from "@/lib/api";

const FREE_SHIP = 100;

export default function Cart() {
  const { items, remove, setQty, subtotal, giftWrap, setGiftWrap } = useCart();
  const { storeOpen } = useStore();
  const navigate = useNavigate();
  const remaining = Math.max(FREE_SHIP - subtotal, 0);
  const pct = Math.min((subtotal / FREE_SHIP) * 100, 100);

  if (items.length === 0) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 py-32 text-center">
          <h1 className="font-serif text-3xl text-charcoal">Your cart is empty</h1>
          <p className="mt-3 text-charcoal/60">Let's find something lovely to gift.</p>
          <Button asChild className="mt-8 bg-charcoal text-white rounded-none hover:bg-gold font-display tracking-wide">
            <Link to="/shop" data-testid="empty-cart-shop-btn">Shop the Boutique</Link>
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-16 py-12">
        <h1 className="font-serif text-4xl text-charcoal mb-10">Shopping Cart</h1>
        <div className="grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-6">
            {items.map((it) => (
              <div key={it.product_id} className="flex gap-4 pb-6 border-b border-soft-border" data-testid={`cart-page-line-${it.product_id}`}>
                <div className="h-28 w-24 shrink-0 overflow-hidden rounded-sm bg-blush">
                  {it.image && <img src={imgUrl(it.image)} alt={it.name} className="h-full w-full object-cover" />}
                </div>
                <div className="flex-1">
                  <div className="flex justify-between">
                    <Link to={`/product/${it.product_id}`} className="font-serif text-lg text-charcoal hover:text-gold">{it.name}</Link>
                    <button onClick={() => remove(it.product_id)} data-testid={`cart-page-remove-${it.product_id}`} className="text-charcoal/40 hover:text-charcoal">
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                  <p className="text-sm text-charcoal/60 mt-1">{cad(it.price)}</p>
                  <div className="mt-4 flex items-center justify-between">
                    <div className="inline-flex items-center border border-soft-border rounded-full">
                      <button onClick={() => setQty(it.product_id, it.quantity - 1)} className="h-8 w-8 grid place-items-center"><Minus className="h-3 w-3" /></button>
                      <span className="w-9 text-center text-sm">{it.quantity}</span>
                      <button onClick={() => setQty(it.product_id, it.quantity + 1)} className="h-8 w-8 grid place-items-center"><Plus className="h-3 w-3" /></button>
                    </div>
                    <span className="font-serif text-lg text-charcoal">{cad(it.price * it.quantity)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white border border-soft-border rounded-sm p-6 space-y-5 sticky top-28">
              <h2 className="font-serif text-xl text-charcoal">Order Summary</h2>
              <div className="bg-blush -mx-6 px-6 py-4">
                {remaining > 0 ? (
                  <p className="text-xs text-charcoal/75">You're <span className="font-semibold text-gold">{cadShort(remaining)}</span> from free shipping</p>
                ) : (
                  <p className="text-xs font-semibold text-gold">🎉 Free shipping unlocked!</p>
                )}
                <div className="mt-2 h-1.5 rounded-full bg-white overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-blush-accent to-gold transition-all duration-500" style={{ width: `${pct}%` }} />
                </div>
              </div>
              <button
                onClick={() => setGiftWrap(!giftWrap)}
                data-testid="cart-page-giftwrap"
                className={`w-full flex items-center gap-3 p-3 rounded-sm border text-left transition-colors ${giftWrap ? "border-gold bg-gold-light" : "border-soft-border"}`}
              >
                <span className={`h-5 w-5 rounded-sm border grid place-items-center ${giftWrap ? "bg-gold border-gold" : "border-charcoal/30"}`}>
                  {giftWrap && <Gift className="h-3 w-3 text-white" />}
                </span>
                <span className="text-sm text-charcoal">Add gift wrapping <span className="text-charcoal/60">(+{cadShort(5)})</span></span>
              </button>
              <div className="flex justify-between text-sm pt-2">
                <span className="text-charcoal/70">Subtotal</span>
                <span className="font-semibold">{cad(subtotal)}</span>
              </div>
              <p className="text-xs text-charcoal/50">Shipping, taxes, promo &amp; gift cards calculated at checkout.</p>
              <Button onClick={() => navigate("/checkout")} disabled={!storeOpen} data-testid="cart-page-checkout-btn" className="w-full h-12 bg-charcoal text-white rounded-none hover:bg-gold font-display tracking-wide disabled:opacity-50 disabled:hover:bg-charcoal">
                {storeOpen ? "Proceed to Checkout" : "Opening Soon"}
              </Button>
              {!storeOpen && <p className="text-xs text-center text-gold" data-testid="cart-closed-note">🌱 We're still stocking our shelves — checkout opens soon.</p>}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
